/**
 * Typed testid registry for the `aiAssistant` domain — the SINGLE source of
 * truth for every `data-testid` this domain renders (TV1-2055, rolling out the
 * TV1-1866 pattern).
 *
 * Components spread these values into `data-testid`; the LIVE Playwright spec
 * (`src/tests/e2e/ai-assistant/assistant.spec.ts`) and the jsdom unit test
 * (`components/__tests__/ApprovalCard.test.tsx`) import the SAME object for
 * their selectors. A renamed key changes the string in ONE place and both sides
 * move together — instead of a runtime "element not found" when a component
 * testid drifts from the string a spec hardcoded (e2e is not run in CI, so that
 * drift fails silently). The `teselly/no-raw-testid` ESLint rule forbids raw
 * `data-testid` literals in this domain so new testids must land here.
 *
 * Scope note: only this domain's two components — `ApprovalCard` and
 * `ReportPreviewCard` — belong here. The chat panel / input / plan-step testids
 * (`ai-assistant-input`, `ai-assistant-send`, `ai-assistant-streaming-indicator`,
 * `ai-assistant-plan-step-*`, `ai-assistant-message-*`) are rendered by
 * `@components/application/AIAssistantChat/*` — OUTSIDE this domain dir — so they
 * live in the shared `@components/testids` registry (`componentsTestIds.aiChat*`),
 * migrated in TV1-2055 phase 3, NOT here.
 *
 * Naming convention: `ai-assistant-[section]-[element]` in kebab-case (see
 * `.claude/rules/e2e-testing.md`). Values are preserved verbatim from the
 * pre-existing literals (no rename) so the migration is value-safe.
 */
export const aiAssistantTestIds = {
  // --- approval card (HITL gate) ---
  /** Pending-approval card container. */
  approvalCard: 'ai-assistant-approval-card',
  /** Humanized action name inside the approval header. */
  approvalActionName: 'ai-assistant-approval-action-name',
  /** Approve button. */
  approvalApprove: 'ai-assistant-approval-approve',
  /** Reject button. */
  approvalReject: 'ai-assistant-approval-reject',

  // --- report preview card (chat report artifact) ---
  /** Report-preview card container. */
  reportPreview: 'ai-assistant-report-preview',
  /** Failure alert (carries `data-reason`). */
  reportPreviewError: 'ai-assistant-report-preview-error',
  /** "Report saved" confirmation text. */
  reportPreviewSaved: 'ai-assistant-report-preview-saved',
  /** Save button. */
  reportPreviewSave: 'ai-assistant-report-preview-save',
  /** Save-failure notice. */
  reportPreviewSaveError: 'ai-assistant-report-preview-save-error'
} as const;
